import { useLocation } from 'wouter';
import { Archive, FileText, Plus, PenTool, Trash2, Undo2, Upload } from 'lucide-react';
import Layout from '@/components/Layout';
import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useTemplateManagement } from '@/hooks/use-template-management';
import { Switch } from '@/components/ui/switch';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

export default function Templates() {
  const [, setLocation] = useLocation();
  
  const {
    templates,
    isLoading,
    showArchived,
    setShowArchived,
    isUploadDialogOpen,
    openUploadDialog,
    closeUploadDialog,
    formData,
    setFormData,
    selectedFile,
    fileInputRef,
    handleFileSelect,
    handleUpload,
    handleDelete,
    confirmDelete,
    cancelDelete,
    deleteTarget,
    setTemplateActive,
    isUploading,
    isDeleting,
    isUpdatingTemplateState,
  } = useTemplateManagement();

  return (
    <Layout>
      <div className="space-y-6 md:px-4 md:py-8">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <PageHeader
            title="Form Templates"
            description="Manage PDF forms to send to homeowners for signing"
            className="max-sm:mb-0"
          />
          <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center sm:gap-4">
            <div className="flex shrink-0 self-start items-center gap-2 max-sm:rounded-md max-sm:border max-sm:bg-muted max-sm:px-3 max-sm:py-2 sm:self-auto">
              <Switch id="show-archived-templates" checked={showArchived} onCheckedChange={setShowArchived} />
              <Label htmlFor="show-archived-templates" className="text-sm">Archived</Label>
            </div>
            <Button onClick={openUploadDialog} className="w-full sm:w-auto">
              <Plus className="mr-2 h-4 w-4" />
              Upload Template
            </Button>
          </div>
        </div>

        {/* Empty State */}
        {!isLoading && templates.length === 0 && (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <FileText className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">{showArchived ? 'No archived templates' : 'No templates yet'}</h3>
              <p className="text-muted-foreground text-center mb-6 max-w-md">
                {showArchived
                  ? 'Archived templates will appear here. Toggle off to return to active templates.'
                  : 'Upload your first PDF template to start collecting signatures and information from homeowners.'}
              </p>
              {!showArchived && (
                <Button onClick={openUploadDialog}>
                  <Upload className="mr-2 h-4 w-4" />
                  Upload Your First Template
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Templates Grid */}
        {templates.length > 0 && (
          <TooltipProvider delayDuration={300}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {templates.map((template) => (
              <Card key={template.id} className="hover:shadow-md transition-shadow overflow-hidden">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <FileText className="h-8 w-8 text-primary" />
                    <div className="flex gap-1">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-9 w-9 sm:h-8 sm:w-8"
                            onClick={() => setLocation(`/templates/${template.id}/editor`)}
                            aria-label={`Edit template ${template.name}`}
                          >
                            <PenTool className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Edit</TooltipContent>
                      </Tooltip>

                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-9 w-9 sm:h-8 sm:w-8"
                            onClick={() => setTemplateActive({ id: template.id, isActive: !template.isActive })}
                            disabled={isDeleting || isUpdatingTemplateState}
                            aria-label={template.isActive ? `Archive template ${template.name}` : `Unarchive template ${template.name}`}
                          >
                            {template.isActive ? <Archive className="h-4 w-4" /> : <Undo2 className="h-4 w-4" />}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>{template.isActive ? 'Archive' : 'Unarchive'}</TooltipContent>
                      </Tooltip>

                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-9 w-9 text-destructive sm:h-8 sm:w-8"
                            onClick={() => handleDelete(template.id, template.name)}
                            disabled={isDeleting || isUpdatingTemplateState}
                            aria-label={`Delete template ${template.name}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Delete</TooltipContent>
                      </Tooltip>
                    </div>
                  </div>
                  <CardTitle className="mt-4 line-clamp-3" title={template.name}>{template.name}</CardTitle>
                  {!template.isActive && (
                    <div className="text-xs text-muted-foreground bg-secondary px-2 py-1 rounded w-fit">Archived</div>
                  )}
                  {template.category && (
                    <div className="text-xs text-muted-foreground bg-secondary px-2 py-1 rounded w-fit">
                      {template.category}
                    </div>
                  )}
                </CardHeader>
                {template.description && (
                  <CardContent>
                    <CardDescription className="line-clamp-3">
                      {template.description}
                    </CardDescription>
                  </CardContent>
                )}
              </Card>
              ))}
            </div>
          </TooltipProvider>
        )}

        {/* Upload Dialog */}
        <Dialog open={isUploadDialogOpen} onOpenChange={closeUploadDialog}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Upload Template</DialogTitle>
              <DialogDescription>
                Upload a PDF form template that homeowners can fill out and sign.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {/* File Input */}
              <div className="space-y-2">
                <Label htmlFor="pdf-file">PDF File *</Label>
                <div className="flex gap-2">
                  <Input
                    id="pdf-file"
                    type="file"
                    accept="application/pdf"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    className="flex-1"
                  />
                </div>
                {selectedFile && (
                  <p className="text-sm text-muted-foreground">
                    Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                  </p>
                )}
              </div>

              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="template-name">Template Name *</Label>
                <Input
                  id="template-name"
                  placeholder="e.g., Homeowner Information Form"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                />
              </div>

              {/* Category */}
              <div className="space-y-2">
                <Label htmlFor="template-category">Category</Label>
                <Input
                  id="template-category"
                  placeholder="e.g., Onboarding, Inspection, Final Walkthrough"
                  value={formData.category}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="template-description">Description</Label>
                <Textarea
                  id="template-description"
                  placeholder="Describe what this template is used for..."
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={closeUploadDialog}
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpload}
                disabled={!selectedFile || !formData.name.trim() || isUploading}
              >
                {isUploading ? 'Uploading...' : 'Upload Template'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && cancelDelete()}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Template</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete &ldquo;{deleteTarget?.name}&rdquo;? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmDelete}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
